/**
 * @file button 模式面板：服务端搜索 + 列表冻结（debounce 未对齐时保留上一屏）
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceFn, useMemoizedFn } from 'ahooks';
import { useKnowledgeBaseList, type IKnowledgeBaseItem } from './useKnowledgeBaseApi';
import { filterKnowledgeBasesWithDocs, KB_PANEL_SEARCH_DEBOUNCE_MS } from '../utils/knowledgeBaseUtils';

interface IUseKbButtonPanelListResult {
  panelKbList: IKnowledgeBaseItem[];
  kbPanelLoading: boolean;
  kbPanelError: boolean;
  panelSearchInput: string;
  handlePanelSearchChange: (keyword: string) => void;
  debouncedSearchKeyword: string;
  fullKbList: IKnowledgeBaseItem[];
  kbLoading: boolean;
  kbError: boolean;
}

/**
 * button 模式知识库列表：全量常驻 + 搜索独立实例
 * @param opened - 面板是否打开
 * @param isButtonMode - 是否 button 模式
 */
export const useKbButtonPanelList = (
  opened: boolean,
  isButtonMode: boolean,
): IUseKbButtonPanelListResult => {
  const [debouncedPanelSearch, setDebouncedPanelSearch] = useState('');
  const [panelSearchInput, setPanelSearchInput] = useState('');
  const stablePanelKbListRef = useRef<IKnowledgeBaseItem[]>([]);

  const { run: notifyPanelSearchChange, cancel: cancelPanelSearchChange } = useDebounceFn(
    (keyword: string) => {
      setDebouncedPanelSearch(keyword.trim());
    },
    { wait: KB_PANEL_SEARCH_DEBOUNCE_MS },
  );

  const handlePanelSearchChange = useMemoizedFn((keyword: string) => {
    setPanelSearchInput(keyword);
    notifyPanelSearchChange(keyword);
  });

  const isPanelSearchSynced = panelSearchInput.trim() === debouncedPanelSearch.trim();
  const isPanelSearchPending = isButtonMode && !isPanelSearchSynced;
  const debouncedSearchKeyword = debouncedPanelSearch.trim();
  const isPanelSearchActive = isButtonMode && Boolean(debouncedSearchKeyword);

  const fullKbListResult = useKnowledgeBaseList();
  const searchKbListResult = useKnowledgeBaseList({
    keyword: debouncedSearchKeyword,
    ready: isPanelSearchActive,
  });

  useEffect(() => {
    if (!isButtonMode || isPanelSearchPending) {
      return;
    }
    const nextList = isPanelSearchActive
      ? searchKbListResult.kbList
      : fullKbListResult.kbList;
    stablePanelKbListRef.current = filterKnowledgeBasesWithDocs(nextList);
  }, [
    isButtonMode,
    isPanelSearchPending,
    isPanelSearchActive,
    searchKbListResult.kbList,
    fullKbListResult.kbList,
  ]);

  const panelKbList = useMemo(() => {
    if (isButtonMode && isPanelSearchPending) {
      if (stablePanelKbListRef.current.length) {
        return stablePanelKbListRef.current;
      }
      return filterKnowledgeBasesWithDocs(fullKbListResult.kbList);
    }
    const source = isPanelSearchActive
      ? searchKbListResult.kbList
      : fullKbListResult.kbList;
    return filterKnowledgeBasesWithDocs(source);
  }, [
    isButtonMode,
    isPanelSearchPending,
    isPanelSearchActive,
    searchKbListResult.kbList,
    fullKbListResult.kbList,
  ]);

  const kbPanelLoading = isButtonMode && panelKbList.length === 0 && (
    fullKbListResult.listLoading
    || isPanelSearchPending
    || (isPanelSearchActive && searchKbListResult.listLoading)
  );

  const kbPanelError = Boolean(isButtonMode && (
    isPanelSearchActive && isPanelSearchSynced
      ? searchKbListResult.error
      : fullKbListResult.error
  ));

  useEffect(() => {
    if (opened) {
      return;
    }
    cancelPanelSearchChange();
    setDebouncedPanelSearch('');
    setPanelSearchInput('');
  }, [opened, cancelPanelSearchChange]);

  return {
    panelKbList,
    kbPanelLoading,
    kbPanelError,
    panelSearchInput,
    handlePanelSearchChange,
    debouncedSearchKeyword,
    fullKbList: fullKbListResult.kbList,
    kbLoading: fullKbListResult.listLoading,
    kbError: fullKbListResult.error,
  };
};
